import React from "react";
import { useTranslation } from "react-i18next";
import { fmtUsd, fmtSigned } from "@/lib/journal";
import { TrendUp, TrendDown, Wallet, Trophy, ChartLineUp } from "@phosphor-icons/react";

const Card = ({ label, value, sub, tone = "neutral", icon: Icon, testid, delay = 0 }) => {
  const toneClass =
    tone === "pos" ? "text-[#4ADE80]" : tone === "neg" ? "text-[#EF4444]" : "text-white";
  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden border border-[#1F1F1F] bg-surface p-6 fade-up transition-colors duration-200 hover:bg-[#111111]"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#C58361] opacity-[0.05] blur-2xl" />
      <div className="flex items-start justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</span>
        {Icon && <Icon size={16} weight="light" className="text-[#C58361]" />}
      </div>
      <div className={`mt-5 font-mono text-2xl sm:text-3xl tracking-tight ${toneClass}`}>{value}</div>
      {sub && <div className="mt-2 text-sm text-zinc-500">{sub}</div>}
    </div>
  );
};

export const MetricCards = ({ a }) => {
  const { t } = useTranslation();
  if (!a) return null;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <Card
        testid="metric-portfolio-volume"
        label={t("metrics.tradeVolume")}
        value={fmtUsd(a.portfolio_volume)}
        sub={t("metrics.tradeVolumeSub", { count: a.total_trades })}
        icon={Wallet}
        delay={0}
      />
      <Card
        testid="metric-total-pnl"
        label={t("metrics.totalPnl")}
        value={fmtSigned(a.total_pnl)}
        sub={t("metrics.pnlSplit", { realized: fmtSigned(a.realized_pnl), unrealized: fmtSigned(a.unrealized_pnl) })}
        tone={a.total_pnl > 0 ? "pos" : a.total_pnl < 0 ? "neg" : "neutral"}
        icon={a.total_pnl >= 0 ? TrendUp : TrendDown}
        delay={80}
      />
      <Card
        testid="metric-win-count"
        label={t("metrics.successfulTrades")}
        value={`${a.win_count} / ${a.closed_trades}`}
        sub={t("metrics.winRateSub", { pct: a.win_rate })}
        icon={Trophy}
        delay={160}
      />
      <Card
        testid="metric-open-value"
        label={t("metrics.openMarketValue")}
        value={fmtUsd(a.open_market_value)}
        sub={t("metrics.openSub", { cost: fmtUsd(a.open_cost_basis), count: a.open_trades })}
        icon={ChartLineUp}
        delay={240}
      />
    </div>
  );
};
